/* ============================================================
   MC BEHAVIORS — shared page behaviours, loaded ONCE globally
   ============================================================
   Bundle of the former mc-reveal.js, mc-rail.js, mc-lazy-video.js
   and mc-footer-reveal.js. Sections used to each emit their own
   <script> tag for these (20 tags / 13 duplicate requests on the
   homepage — PSI "Duplicated JavaScript"); now a single deferred
   include in snippets/scripts.liquid serves every template.

   Each behaviour keeps its original window.__mc*Init guard so any
   stale per-section include (cached HTML, unmigrated branch) is a
   harmless no-op. Behaviours re-scan on shopify:section:load so
   sections added or re-rendered in the theme editor still work.
   ============================================================ */

/* ------------------------------------------------------------
   1. REVEAL — scroll-reveal via IntersectionObserver
   Adds .is-revealed to [data-reveal] / [data-reveal-image] when
   they enter view. Mirrors the reference script.js initReveal().
   ------------------------------------------------------------ */
(function () {
  'use strict';
  if (window.__mcRevealInit) return;
  window.__mcRevealInit = true;

  var observer = null;
  // Identity-based guard, NOT a data-attribute: Horizon's morph (section
  // re-renders, theme editor, hot reload) clones nodes — a cloned node
  // would keep a data-attribute flag while losing its actual observer
  // registration, leaving it permanently unrevealed.
  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;
  function isObserved(el) { return observed ? observed.has(el) : false; }
  function markObserved(el) { if (observed) observed.add(el); }

  function revealAll() {
    document.querySelectorAll('[data-reveal], [data-reveal-image]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
  }

  function start() {
    var els = document.querySelectorAll('[data-reveal], [data-reveal-image]');
    if (!els.length) return;

    // No IntersectionObserver / reduced motion → show everything immediately.
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || reduce) {
      revealAll();
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (entry.target.hasAttribute('data-mc-rail-scroller')) {
            // Reveal every element inside a rail at once: items sitting
            // beyond the visible rail edge never intersect the viewport,
            // so per-item reveal left them blank when scrolled in.
            entry.target.querySelectorAll('[data-reveal], [data-reveal-image]').forEach(function (el) {
              el.classList.add('is-revealed');
            });
          } else {
            entry.target.classList.add('is-revealed');
          }
          observer.unobserve(entry.target);
        });
        // Reveal 200px BEFORE entering the viewport (was -8%, i.e. after):
        // content is already visible when the user reaches it.
      }, { rootMargin: '0px 0px 200px 0px', threshold: 0 });
    }

    els.forEach(function (el) {
      if (isObserved(el) || el.classList.contains('is-revealed')) return;
      markObserved(el);
      var rail = el.closest('[data-mc-rail-scroller]');
      if (rail) {
        if (!isObserved(rail)) {
          markObserved(rail);
          observer.observe(rail);
        }
      } else {
        observer.observe(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
  window.__mcRevealRescan = start;
})();

/* ------------------------------------------------------------
   2. RAIL — horizontal carousel prev/next controls
   Buttons scroll the nearest [data-mc-rail-scroller] by one item
   width. Delegated listener: no per-button binding, and sections
   added later work without re-init.
   ------------------------------------------------------------ */
(function () {
  'use strict';
  if (window.__mcRailInit) return;
  window.__mcRailInit = true;

  function stepDistance(scroller) {
    var first = scroller.firstElementChild;
    if (first) {
      var styles = window.getComputedStyle(scroller);
      var gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
      var w = first.offsetWidth + gap;
      if (w > 0) return w;
    }
    return Math.max(280, scroller.clientWidth * 0.75);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('[data-mc-rail-prev], [data-mc-rail-next]');
    if (!btn) return;
    var rail = btn.closest('[data-mc-rail]');
    var scroller = rail ? rail.querySelector('[data-mc-rail-scroller]') : null;
    if (!scroller) return;
    e.preventDefault();
    var dir = btn.hasAttribute('data-mc-rail-prev') ? -1 : 1;
    scroller.scrollBy({ left: dir * stepDistance(scroller), behavior: 'smooth' });
  });
})();

/* ------------------------------------------------------------
   3. LAZY VIDEO — defer <video> loading via IntersectionObserver
   Videos from snippet 'mc-lazy-video' and the hand-rendered
   slideshow slides ship with NO src: each <source> carries
   data-src. Near the viewport we copy data-src -> src, call
   load() once, and play() if the element autoplays.

   Nothing is observed — so nothing downloads — before the user's
   first interaction (window.__mcFirstInteraction, registered inline
   in snippets/scripts.liquid): a hero in view at load keeps its
   poster until the visitor scrolls, wheels, touches, clicks or
   presses a key. The theme editor bypasses the gate.

   State lives on the marker attribute, not in module memory, so
   it survives morphs and re-init:
     data-mc-lazy-video=""         pending (observed)
     data-mc-lazy-video="loaded"   sources attached
     data-mc-lazy-video="failed"   media error, never retried
   ------------------------------------------------------------ */
(function () {
  'use strict';
  if (window.__mcLazyVideoInit) return;
  window.__mcLazyVideoInit = true;

  // Start fetching this far before the video enters the viewport.
  var ROOT_MARGIN = '300px 0px';
  var SELECTOR = 'video[data-mc-lazy-video]';
  var observer = null;

  function isPending(video) {
    return video.getAttribute('data-mc-lazy-video') === '';
  }

  function inDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* First-interaction gate. Open in the theme editor (merchants must see
     live video) and when the inline gate script is absent (behave as before). */
  function armed() {
    if (inDesignMode()) return true;
    var gate = window.__mcFirstInteraction;
    return !gate || gate.happened;
  }
  function whenArmed(fn) {
    if (armed()) fn();
    else window.__mcFirstInteraction.promise.then(fn);
  }

  // The snippet marks autoplay with data-autoplay; hand-rendered slides
  // keep the native attribute. Honour both.
  function wantsAutoplay(video) {
    return video.hasAttribute('autoplay') || 'autoplay' in video.dataset;
  }

  function playSafely(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  /* Attach sources and load once. Idempotent — the marker attribute is
     the guard.

     Every visitor loads, unconditionally. Reduced-motion and Save-Data used
     to leave the poster in place here; per client decision (2026-09-21) they
     no longer do, because a permanently-postered hero reads as a broken page
     — those two settings are far more common on phones, so the skip showed up
     as "the banner video doesn't work on mobile". See watchMotionPreference()
     below for the one accessibility affordance that remains. */
  function loadVideo(video) {
    if (!isPending(video)) return;

    var attached = 0;
    var sources = video.querySelectorAll('source[data-src]');
    for (var i = 0; i < sources.length; i++) {
      var url = sources[i].getAttribute('data-src');
      sources[i].removeAttribute('data-src');
      if (!url) continue; // empty or already consumed — skip safely
      sources[i].setAttribute('src', url);
      attached++;
    }
    // Support data-src directly on the <video> as well.
    if (!attached && video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
      video.removeAttribute('data-src');
      attached++;
    }
    if (!attached) {
      video.setAttribute('data-mc-lazy-video', 'failed');
      return; // nothing to load
    }

    video.setAttribute('data-mc-lazy-video', 'loaded');

    /* Failure detection. With <source> children the browser fires `error`
       at each rejected <source> — never at the <video> — and, once the list
       is exhausted, parks at NETWORK_NO_SOURCE without setting video.error.
       Those events don't bubble, but the capture phase still passes through
       the ancestor, so one capturing listener on the video sees both a media
       error and the last source being rejected. Poster stays put; mark it
       and never retry. */
    var allSources = video.querySelectorAll('source'); // DOM order = the browser's try order
    var lastSource = allSources.length ? allSources[allSources.length - 1] : null;
    var onError = function (e) {
      if (e.target !== video && e.target !== lastSource) return; // earlier source; browser tries the next
      video.removeEventListener('error', onError, true);
      video.setAttribute('data-mc-lazy-video', 'failed');
    };
    video.addEventListener('error', onError, true);

    video.load();

    if (wantsAutoplay(video)) {
      // muted is required for programmatic autoplay to be allowed.
      video.muted = true;
      if (video.readyState >= 2) {
        playSafely(video);
      } else {
        video.addEventListener('canplay', function () { playSafely(video); }, { once: true });
      }
    }
  }

  // One observer for every video on the page.
  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      observer.unobserve(entries[i].target);
      loadVideo(entries[i].target);
    }
  }
  function getObserver() {
    if (!observer) {
      observer = new IntersectionObserver(onIntersect, { rootMargin: ROOT_MARGIN, threshold: 0.01 });
    }
    return observer;
  }

  /* Observe every pending video under `root` (default: document).
     Idempotent: observe() ignores targets it already tracks and loadVideo
     is guarded by the marker, so call it as often as you like. Cost is one
     querySelectorAll on `root` — scope it to what actually changed. Before
     the first interaction it only queues itself. */
  function scan(root) {
    if (!armed()) {
      whenArmed(function () { scan(root); });
      return;
    }
    var scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    var videos = Array.prototype.slice.call(scope.querySelectorAll(SELECTOR));
    if (scope !== document && typeof scope.matches === 'function' && scope.matches(SELECTOR)) videos.push(scope);
    if (!videos.length) return;

    // No IntersectionObserver, or the theme editor → load right away.
    var eager = typeof IntersectionObserver === 'undefined' || inDesignMode();

    for (var i = 0; i < videos.length; i++) {
      if (!isPending(videos[i])) continue;
      if (eager) loadVideo(videos[i]);
      else getObserver().observe(videos[i]);
    }
  }

  /* If the user turns reduced-motion ON mid-session, stop anything already
     playing and hand them controls. Deliberately one-way: turning it back
     OFF does not auto-start videos, because spontaneously starting motion is
     the surprise we're trying to avoid.

     Kept deliberately after the 2026-09-21 change removed the load-time
     reduced-motion skip: this is now the ONLY WCAG 2.2.2 (Pause, Stop, Hide)
     affordance on decorative video. It does not fire for visitors who already
     had the preference on before load — those users get an autoplaying loop
     with no pause control, which is a known, accepted AA gap. */
  function watchMotionPreference() {
    if (!window.matchMedia) return;
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onChange = function () {
      if (!mq.matches) return;
      var loaded = document.querySelectorAll('video[data-mc-lazy-video="loaded"]');
      for (var i = 0; i < loaded.length; i++) {
        if (!wantsAutoplay(loaded[i])) continue;
        loaded[i].controls = true;
        loaded[i].pause();
      }
    };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else if (typeof mq.addListener === 'function') mq.addListener(onChange); // Safari < 14
  }

  function onEditorEvent(e) {
    scan(e && e.target);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Theme editor re-renders / selections: scope the scan to what changed.
  document.addEventListener('shopify:section:load', onEditorEvent);
  document.addEventListener('shopify:section:select', onEditorEvent);
  document.addEventListener('shopify:block:select', onEditorEvent);

  watchMotionPreference();

  // The only entry point: everything goes through the viewport and
  // first-interaction gates. Nothing may attach sources directly.
  window.__mcLazyVideoScan = scan;
})();

/* ------------------------------------------------------------
   5. HOVER IMAGES — load hover-swap panes on first pointerenter
   Cards/lookbook render their hover image with data-src/data-srcset
   (no src), so touch devices — where :hover can never fire — skip
   the download entirely. On hover-capable devices the swap happens
   on the first pointerenter of the [data-mc-hover-root] container.
   ------------------------------------------------------------ */
(function () {
  'use strict';
  if (window.__mcHoverImgInit) return;
  window.__mcHoverImgInit = true;

  var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var armed = typeof WeakSet === 'function' ? new WeakSet() : null;

  function swap(img) {
    if (img.dataset.srcset) {
      img.setAttribute('srcset', img.dataset.srcset);
      img.removeAttribute('data-srcset');
    }
    if (img.dataset.src) {
      img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-src');
    }
  }

  function swapAll(root) {
    root.querySelectorAll('img[data-mc-hover-img][data-src]').forEach(swap);
  }

  function start() {
    document.querySelectorAll('[data-mc-hover-root]').forEach(function (root) {
      if (armed) {
        if (armed.has(root)) return;
        armed.add(root);
      }

      if (canHover) {
        // Desktop: load the alternate image on first hover; CSS :hover reveals it.
        root.addEventListener('pointerenter', function () { swapAll(root); }, { once: true });
        return;
      }

      // Touch (no :hover): mirror the desktop hover — the first tap on the card
      // image loads + reveals the alternate image (via .is-peek); a second tap
      // on it (or a tap on the product title) opens the product. Only product
      // cards expose .mc-card__media-link, so lookbook cards stay hover-only.
      var mediaLink = root.querySelector('.mc-card__media-link');
      if (!mediaLink || !root.querySelector('img[data-mc-hover-img]')) return;
      mediaLink.addEventListener('click', function (e) {
        if (root.classList.contains('is-peek')) return; // 2nd tap → navigate
        e.preventDefault();
        swapAll(root);
        var open = document.querySelector('.mc-card.is-peek');
        if (open && open !== root) open.classList.remove('is-peek');
        root.classList.add('is-peek');
      });
    });
  }

  // Tapping outside a peeked card returns it to the first image.
  if (!canHover) {
    document.addEventListener('click', function (e) {
      var open = document.querySelector('.mc-card.is-peek');
      if (open && !open.contains(e.target)) open.classList.remove('is-peek');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
  window.__mcHoverImgRescan = start;
})();

/* ------------------------------------------------------------
   RE-SCAN ON DOM MORPHS
   Horizon re-renders sections by morphing DOM (section-renderer
   on filter/cart updates, theme editor, dev hot reload). Morphed
   clones lose their IntersectionObserver registration, so a
   debounced mutation watcher re-arms reveal + hover images. The
   re-scan is two querySelectorAlls; WeakSet guards keep already
   -observed elements free.
   Lazy video is deliberately NOT re-scanned from here: a full-
   document query on every mutation is the wrong cost model, the
   theme-editor events (section:load/select, block:select) already
   cover re-renders, and no morphed surface (facets, cart, product
   recommendations) renders a lazy video. Anything that injects one
   elsewhere calls window.__mcLazyVideoScan(root) itself.
   ------------------------------------------------------------ */
(function () {
  'use strict';
  if (window.__mcBehaviorsRescanInit || typeof MutationObserver !== 'function') return;
  window.__mcBehaviorsRescanInit = true;

  var timer = null;

  function arm() {
    var main = document.getElementById('MainContent') || document.body;
    new MutationObserver(function () {
      if (timer) return;
      timer = setTimeout(function () {
        timer = null;
        if (window.__mcRevealRescan) window.__mcRevealRescan();
        if (window.__mcHoverImgRescan) window.__mcHoverImgRescan();
      }, 300);
    }).observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
})();

/* ------------------------------------------------------------
   4. FOOTER REVEAL — fixed footer the page content lifts off of
   Reserves space equal to the fixed footer's height as
   margin-bottom on #MainContent. No-op when the footer isn't
   fixed (mobile / non-reveal templates).
   ------------------------------------------------------------ */
(function () {
  'use strict';
  if (window.__mcFooterRevealInit) return;
  window.__mcFooterRevealInit = true;

  function update() {
    var footer = document.querySelector('footer');
    var main = document.getElementById('MainContent');
    if (!footer || !main) return;

    if (getComputedStyle(footer).position === 'fixed') {
      main.style.marginBottom = (Math.ceil(footer.getBoundingClientRect().height) - 2) + 'px';
    } else if (main.style.marginBottom) {
      main.style.marginBottom = '';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', update);
  } else {
    update();
  }

  // Recompute once images/fonts settle and on viewport changes
  window.addEventListener('load', update);
  window.addEventListener('resize', update);
  document.addEventListener('shopify:section:load', update);

  // The footer height can change after web fonts load
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(update);
  }
})();

/* ------------------------------------------------------------
   5. NEWSLETTER POPUP — sections/mc-newsletter-popup.liquid

   Nothing here runs, and the popup image is not fetched, before the
   visitor's first genuine interaction — scroll, cursor/pointer move,
   click, touch or key. Same gate the lazy video uses
   (window.__mcFirstInteraction, registered inline in
   snippets/scripts.liquid); the theme editor bypasses it.

   On release: attach the image (data-src -> src, as LAZY VIDEO does
   for <source data-src>), wire the listeners, then start the delay
   countdown. So the delay setting is measured from first interaction,
   not from page load.

   Opens at most once per `reshow_days`. Dismiss via close button,
   overlay, ESC or the dismiss link; the sticky tab re-opens it on
   demand. Focus is trapped while open and restored on close.

   The subscribe form is Shopify's native customer form, so submitting
   reloads the page; when the response carries this form's hash we wire
   and open immediately — the visitor just acted, and they need to see
   whether it worked.
   ------------------------------------------------------------ */
(function () {
  'use strict';
  if (window.__mcPopupInit) return;
  window.__mcPopupInit = true;

  var STORAGE_KEY = 'mc_popup_dismissed_at';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var popup = null;
  var overlay = null;
  var lastFocused = null;
  var openTimer = null;
  var keydownBound = false;

  function store(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch (e) {
      /* private mode / storage disabled — popup just re-shows next visit */
    }
  }

  function read(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function inDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* First-interaction gate — mirrors LAZY VIDEO (§3). Open in the theme
     editor (merchants must see the popup) and when the inline gate script
     is absent, so a missing gate degrades to the old timer behaviour. */
  function armed() {
    if (inDesignMode()) return true;
    var gate = window.__mcFirstInteraction;
    return !gate || gate.happened;
  }
  function whenArmed(fn) {
    if (armed()) fn();
    else window.__mcFirstInteraction.promise.then(fn);
  }

  /* Has the visitor dismissed or subscribed inside the re-show window? */
  function isSuppressed() {
    var reshow = parseInt(popup.getAttribute('data-mc-popup-reshow'), 10);
    var at = parseInt(read(STORAGE_KEY), 10);
    if (!at) return false;
    if (!reshow) return true; // 0 = never show automatically again
    return Date.now() - at < reshow * 86400000;
  }

  /* Attach the deferred image. Marker attribute, not module memory, so it
     survives morphs and re-init. */
  function loadMedia() {
    var img = popup.querySelector('img[data-mc-popup-image]');
    if (!img || img.getAttribute('data-mc-popup-image') === 'loaded') return;
    var srcset = img.getAttribute('data-srcset');
    var src = img.getAttribute('data-src');
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
    img.setAttribute('data-mc-popup-image', 'loaded');
  }

  function open() {
    if (!popup || popup.classList.contains('is-open')) return;
    loadMedia();
    lastFocused = document.activeElement;
    popup.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('mc-popup-open');

    var target =
      popup.querySelector('[autofocus]') ||
      popup.querySelector('input[type="email"]') ||
      popup.querySelector('[data-mc-popup-close]');
    if (target) target.focus();
  }

  function close(remember) {
    if (!popup || !popup.classList.contains('is-open')) return;
    popup.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('mc-popup-open');
    if (remember !== false) store(STORAGE_KEY, String(Date.now()));
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function trapFocus(e) {
    if (e.key !== 'Tab' || !popup.classList.contains('is-open')) return;
    var items = Array.prototype.filter.call(popup.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* Idempotent: the marker lives on the element so a re-init (theme editor
     section reload, morph) doesn't stack duplicate listeners. */
  function wire() {
    if (popup.getAttribute('data-mc-popup-wired') === 'true') return;
    popup.setAttribute('data-mc-popup-wired', 'true');

    document.querySelectorAll('[data-mc-popup-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });

    popup.querySelectorAll('[data-mc-popup-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        close(true);
      });
    });

    if (overlay) {
      overlay.addEventListener('click', function () {
        close(true);
      });
    }

    if (!keydownBound) {
      keydownBound = true;
      document.addEventListener('keydown', function (e) {
        if (!popup) return;
        if (e.key === 'Escape') close(true);
        else trapFocus(e);
      });
    }
  }

  function init() {
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }
    popup = document.querySelector('[data-mc-popup]');
    overlay = document.querySelector('[data-mc-popup-overlay]');
    if (!popup) return;

    /* The customer form posted: Shopify redirects back with this form's
       hash, so wire up and re-open at once to surface the success message
       or the field errors. Success counts as "handled" — don't nag the
       subscriber again. Deliberately ungated: the visitor just submitted. */
    var formId = popup.getAttribute('data-mc-popup-form-id');
    if (formId && window.location.hash === '#' + formId) {
      wire();
      if (popup.querySelector('.mc-popup__success')) store(STORAGE_KEY, String(Date.now()));
      open();
      return;
    }

    /* In the theme editor, show it whenever the section is selected rather
       than obeying the delay and the dismissal flag. */
    if (inDesignMode()) {
      wire();
      document.addEventListener('shopify:section:select', function (e) {
        if (popup && e.target.contains(popup)) open();
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (popup && e.target.contains(popup)) close(false);
      });
    }

    var delay = parseInt(popup.getAttribute('data-mc-popup-delay'), 10);
    if (isNaN(delay)) delay = 5;

    /* Gate first, THEN the delay: the countdown starts at the visitor's
       first interaction, not at page load.

       A visitor inside the re-show window still gets the wiring — the
       sticky tab has to open the popup on demand — but no timer and no
       image; open() fetches it if they actually ask for the popup. */
    whenArmed(function () {
      if (!popup) return;
      wire();
      if (isSuppressed()) return;
      loadMedia();
      openTimer = setTimeout(open, delay * 1000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('[data-mc-popup]')) init();
  });
})();
